import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { map, mergeMap } from 'rxjs/operators';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { componentScope } from '../../../../../../../constants/component-scope';
import { BusMessageChannels, BusMessageService } from '../../../../../../../services/bus-message.service';
import { IUserServerResponse } from '../../../../../../../../../shared/interfaces/user.interface';
import { UserService } from '../../../../../../../services/user.service';
import { $user } from '../../../../../../../../../shared/constants/user';
import { CustomValidators } from '../../../../../../../services/helpers/form-custom-validators';
import { $formFields } from '../constant/form-fields';
import { CommentService } from '../../../../../../../services/comment.service';
import { CommonUtility } from '../../../../../../../utility/common.utility';
import {
  IComment,
  IManualAuditIssueCommentPostClientRequestBody,
} from '../../../../../../../../../shared/interfaces/comment.interface';
import { $manualAuditIssueComment } from '../../../../../../../../../shared/constants/comment';
import { LoadErrorHandler } from '../../../../../../../services/load-error-handler';
import { SharedCommonUtility } from '../../../../../../../../../shared/utils/common.utility';
import { RequiredSecurities } from '../../../../../../../../../shared/constants/required-securities';
import { AclSecurityAdapter } from '../../../../../../../services/acl.service';
import { UserAclService } from '../../../../../../../services/user-acl.service';
import { Origin } from '../../../../../../../../../shared/interfaces/scan.interface';

@Component({
  selector: 'app-post-manual-audit-issue-comment',
  templateUrl: './post-manual-audit-issue-comment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PostManualAuditIssueCommentComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  private formValidationRequest: Subject<void>;

  @Input() public scope: componentScope;
  @Input() public workspaceId: string;
  @Input() public digitalPropertyId: string;
  @Input() public manualAuditIssueId: string;
  @Input() public manualAuditFindingId: string;
  @Input() public evaluationScanId: string;

  @Output() public postedComment: EventEmitter<IComment>;

  public form: UntypedFormGroup;
  public userData: IUserServerResponse;
  public username: string;
  public userOrigin$: Observable<Origin>;

  public $formFields: typeof $formFields;
  public RequiredSecurities: typeof RequiredSecurities = RequiredSecurities;
  public Origin: typeof Origin = Origin;

  public formValidationRequest$: Observable<void>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private busMessageService: BusMessageService,
    private userService: UserService,
    private commentService: CommentService,
    private changeDetectorRef: ChangeDetectorRef,
    private loadErrorHandler: LoadErrorHandler,
    private userAclService: UserAclService,
  ) {
    this.subscriptions = new Subscription();
    this.formValidationRequest = new Subject<void>();

    this.userData = null;
    this.username = '';
    this.$formFields = $formFields;
    this.postedComment = new EventEmitter<IComment>();
  }

  private createForm(showInternalCheckbox: boolean): void {
    const formConfig: Record<string, UntypedFormControl> = {
      [$formFields.message]: new UntypedFormControl('', {
        validators: [CustomValidators.required, CustomValidators.validateIsEmpty],
      }),
      [$formFields.screenshot]: new UntypedFormControl('', {
        validators: [CustomValidators.domainValidator],
      }),
      [$formFields.recommendation]: new UntypedFormControl(''),
      ...(showInternalCheckbox ? { [$formFields.internal]: new UntypedFormControl(false) } : {}),
    };
    this.form = this.formBuilder.group(formConfig);
    this.changeDetectorRef.detectChanges();
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError(message, response, PostManualAuditIssueCommentComponent.name);
  }

  private onGetMeProfileSuccess(me: IUserServerResponse): void {
    this.userData = me;
    this.username = me[$user.displayName];
    this.changeDetectorRef.detectChanges();
  }

  private onGetMeProfileError(error: HttpErrorResponse): void {
    this.handleError('error_getting_me_profile', error);
  }

  private emitPostComment(): void {
    this.postedComment.emit();
    this.clear();
  }

  private getForm(): FormData {
    const angularFormData: Partial<IManualAuditIssueCommentPostClientRequestBody> = {
      [$manualAuditIssueComment.message]: this.form.get($formFields.message).value as string,
      [$manualAuditIssueComment.manualAuditFindingId]: this.manualAuditFindingId,
      ...(SharedCommonUtility.notNullish(this.form.get($formFields.internal)?.value)
        ? { [$manualAuditIssueComment.internal]: this.form.get($formFields.internal).value }
        : {}),
    };
    return CommonUtility.createFormData(angularFormData);
  }

  public clear(): void {
    this.form.reset();
  }

  public submit(): void {
    this.formValidationRequest.next();
    if (this.form.valid === false) {
      return;
    }
    this.subscriptions.add(
      this.commentService
        .postManualAuditIssueComment(
          this.getForm(),
          this.workspaceId,
          this.digitalPropertyId,
          this.evaluationScanId,
          this.manualAuditIssueId,
        )
        .subscribe({
          next: this.emitPostComment.bind(this),
          error: this.handleError.bind(this),
        }),
    );
  }

  public ngOnInit(): void {
    this.formValidationRequest$ = this.formValidationRequest.asObservable();
    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.userSwitchedToDigitalProperty)
        .pipe(mergeMap((): Observable<IUserServerResponse> => this.userService.getAuthenticatedUserProfile()))
        .subscribe({
          next: this.onGetMeProfileSuccess.bind(this),
          error: this.onGetMeProfileError.bind(this),
        }),
    );

    this.userOrigin$ = this.userService.getUserOrigin();
    const canCreateInternalComments$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useWorkspaceFromUser()
            .useDigitalPropertyFromUser()
            .useFunctionalActions(RequiredSecurities.MT_Manual_Finding_Review_Create.functionalActions)
            .check(),
        ),
      );
    this.subscriptions.add(
      combineLatest([canCreateInternalComments$, this.userOrigin$]).subscribe(
        ([canCreateInternalComments, userOrigin]: [boolean, Origin]) => {
          this.createForm(canCreateInternalComments && userOrigin === Origin.staff);
        },
      ),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
